// Route Registry - Single source of truth for SEO metadata
// All routes that should be indexed are listed here

export const SITE_URL = 'https://aquaquote.com';
export const SITE_NAME = 'AquaQuote';
export const DEFAULT_OG_IMAGE = 'https://storage.googleapis.com/gpt-engineer-file-uploads/X5ifO2qR1ZTuDNPDuamnXz0pdaz1/social-images/social-1760992550322-Screenshot 2025-10-20 at 1.07.12 PM.png';

export interface RouteMeta {
  title: string;
  description: string;
  canonical: string;
  noIndex?: boolean;
  priority?: number;
  changefreq?: 'always' | 'hourly' | 'daily' | 'weekly' | 'monthly' | 'yearly' | 'never';
  ogImage?: string;
  ogType?: string;
  schemaType?: 'Organization' | 'WebSite' | 'Product' | 'Article' | 'FAQPage' | 'CollectionPage';
}

// Static routes metadata
export const staticRoutes: Record<string, RouteMeta> = {
  '/': {
    title: 'AquaQuote - Industrial RO Membranes & Pressure Vessels',
    description: 'Trusted supplier of industrial and commercial RO membranes and pressure vessels. Serving SpaceX, P&G, DuPont. Authorized distributor of DuPont FilmTec, Hydranautics, Toray, LG.',
    canonical: SITE_URL,
    priority: 1.0,
    changefreq: 'weekly',
    schemaType: 'Organization',
  },
  '/products': {
    title: 'RO Membranes & Pressure Vessels Catalog | AquaQuote',
    description: 'Browse our complete catalog of industrial RO membranes and pressure vessels. DuPont FilmTec, Hydranautics, Toray, LG, and more. Fast quotes and competitive pricing.',
    canonical: `${SITE_URL}/products`,
    priority: 0.9,
    changefreq: 'daily',
    schemaType: 'CollectionPage',
  },
  '/ro-membranes': {
    title: 'Industrial RO Membranes | DuPont FilmTec, Hydranautics, Toray | AquaQuote',
    description: 'Shop industrial reverse osmosis membranes from leading brands. DuPont FilmTec, Hydranautics, Toray, LG Chem. All sizes and configurations. Expert support.',
    canonical: `${SITE_URL}/ro-membranes`,
    priority: 0.8,
    changefreq: 'daily',
    schemaType: 'CollectionPage',
  },
  '/pressure-vessels': {
    title: 'FRP Pressure Vessels | Codeline, Pentair, Wave Cyber | AquaQuote',
    description: 'Industrial FRP pressure vessels for RO systems. Codeline, Pentair, Wave Cyber. 2.5" to 8" diameters, all pressure ratings. Quick quotes available.',
    canonical: `${SITE_URL}/pressure-vessels`,
    priority: 0.8,
    changefreq: 'daily',
    schemaType: 'CollectionPage',
  },
  '/about': {
    title: 'About AquaQuote | 12 Years of Water Treatment Expertise',
    description: 'AquaQuote has served the water treatment industry for 12 years. Headquartered in San Diego, CA with multiple warehouse locations. Trusted by SpaceX, P&G, and DuPont.',
    canonical: `${SITE_URL}/about`,
    priority: 0.7,
    changefreq: 'monthly',
  },
  '/contact': {
    title: 'Contact AquaQuote | Get a Quote for RO Membranes & Vessels',
    description: 'Contact AquaQuote for industrial RO membrane and pressure vessel quotes. Phone: +1 858-815-6800. Email: orders@aquaquote.com. Fast response guaranteed.',
    canonical: `${SITE_URL}/contact`,
    priority: 0.7,
    changefreq: 'monthly',
  },
  '/blog': {
    title: 'Water Treatment Blog | Industry Insights | AquaQuote',
    description: 'Expert insights on RO membranes, pressure vessels, and water treatment systems. Technical guides, industry news, and maintenance tips from AquaQuote.',
    canonical: `${SITE_URL}/blog`,
    priority: 0.6,
    changefreq: 'weekly',
    schemaType: 'CollectionPage',
  },
  // Private routes - must be noindexed
  '/admin': {
    title: 'Admin Dashboard | AquaQuote',
    description: 'AquaQuote admin dashboard',
    canonical: `${SITE_URL}/admin`,
    noIndex: true,
  },
  '/admin/login': {
    title: 'Admin Login | AquaQuote',
    description: 'AquaQuote admin login',
    canonical: `${SITE_URL}/admin/login`,
    noIndex: true,
  },
};

// Dynamic route metadata generators
export const generateProductMeta = (product: {
  title: string;
  description?: string;
  handle: string;
  vendor?: string;
  productType?: string;
  images?: { url: string }[];
  priceRange?: { minVariantPrice: { amount: string; currencyCode: string } };
}): RouteMeta => {
  const cleanDescription = product.description
    ? product.description.replace(/<[^>]*>/g, '').slice(0, 155) + '...'
    : `Shop ${product.title} from ${product.vendor || 'AquaQuote'}. Industrial water treatment equipment with fast shipping.`;

  // Determine category based on product type/title
  const type = (product.productType || '').toLowerCase();
  const productTitle = (product.title || '').toLowerCase();
  const category = (type.includes('vessel') || productTitle.includes('vessel') || productTitle.includes('pressure'))
    ? 'pressurevessels'
    : 'romembranes';
  
  // Normalize brand for URL
  const brand = product.vendor
    ? product.vendor.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
    : 'unknown';

  return {
    title: `${product.title} | ${product.vendor || 'AquaQuote'}`,
    description: cleanDescription,
    canonical: `${SITE_URL}/${category}/${brand}/${product.handle}`,
    priority: 0.7,
    changefreq: 'weekly',
    ogImage: product.images?.[0]?.url || DEFAULT_OG_IMAGE,
    schemaType: 'Product',
  };
};

export const generateBlogPostMeta = (post: {
  title: string;
  excerpt?: string;
  slug: string;
  image_url?: string;
  author?: string;
  created_at?: string;
}): RouteMeta => {
  return {
    title: `${post.title} | AquaQuote Blog`,
    description: post.excerpt || `Read ${post.title} on the AquaQuote blog. Expert insights on water treatment.`,
    canonical: `${SITE_URL}/blog/${post.slug}`,
    priority: 0.5,
    changefreq: 'monthly',
    ogImage: post.image_url || DEFAULT_OG_IMAGE,
    ogType: 'article',
    schemaType: 'Article',
  };
};

// Get all indexable routes for sitemap generation
export const getIndexableRoutes = (): Array<{ path: string; meta: RouteMeta }> => {
  return Object.entries(staticRoutes)
    .filter(([_, meta]) => !meta.noIndex)
    .map(([path, meta]) => ({ path, meta }));
};
